import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import { FileSourceParametersSettings, IngestionSourceParametersOutput } from '../../../../../../gamma-api';
import IbTypography from '../../../../../components/common/IbTypography';
import IbInput from '../../../../../components/common/IbInput';
import FileUpload from '../../../../../components/common/FileUpload';
import { formatFileCount } from '../../../../../utils/fileUtil';
import FileTable, { FileTableStore, IFileTableItem } from '../FileTable';
import { FormAPI } from '../../../../../types';
import IbFormFieldLabel from '../../../../../components/common/IbFormFieldLabel';
import IbButton from '../../../../../components/common/IbButton';
import IbIcon from '../../../../../components/common/IbIcon';

import { FILE_UPLOAD_BUTTON_CLASS_NAME, MAIN_CLASS_NAME } from './const';
import { useValidationHandle } from './useValidationHandle';

const ACCEPTED_FILE_TYPES = ['.txt', '.html', '.md', '.docx', '.doc'].join(',');

export interface ISourceSettingsFileProps {
  loading: boolean;
  parameters: IngestionSourceParametersOutput;
  files: IFileTableItem[];
  onChange?: (parameters: IngestionSourceParametersOutput) => void;
  onFileCreate?: (files: RcFile[], store: FileTableStore) => Promise<void>;
  onFileDelete?: (files: IFileTableItem[], store: FileTableStore) => Promise<void>;
}

const SourceSettingsFile = forwardRef<FormAPI, ISourceSettingsFileProps>(
  ({ loading, parameters, files, onChange, onFileCreate, onFileDelete }, ref) => {
    const { t } = useTranslation();

    const [errors, setErrors] = useValidationHandle(
      ref,
      (isTouched: boolean) => {
        return {
          name: { isTouched: isTouched, isError: !parameters.name },
          files: { isTouched: isTouched, isError: !files.length },
        };
      },
      [parameters.name, files]
    );

    useUpdateEffect(() => {
      setErrors({ ...errors, files: { isError: !files.length, isTouched: true } });
    }, [files]);

    const onNameChange = (value: string) => {
      onChange?.({
        ...parameters,
        name: value,
      });

      setErrors({ ...errors, name: { isError: !value, isTouched: true } });
    };

    const onNameBlur = () => setErrors({ ...errors, name: { isError: errors.name.isError, isTouched: true } });

    const onFileUpload = async (multipleFiles: RcFile[]) => {
      if (!onFileCreate) {
        return false;
      }

      if (!parameters.name) {
        onChange?.({
          ...parameters,
          name: multipleFiles[0].name,
        });
      }

      const store = FileTableStore.Server;
      await onFileCreate(multipleFiles, store);

      return false;
    };

    const onDeleteClick = async (singleFile: IFileTableItem) => {
      if (!onFileDelete) {
        return;
      }

      const settings = parameters.settings as FileSourceParametersSettings;
      const store =
        singleFile.server && settings.files?.some((file) => file.file_id === singleFile.server?.file_id)
          ? FileTableStore.Client
          : FileTableStore.Server;
      await onFileDelete([singleFile], store);
    };

    return (
      <div className={MAIN_CLASS_NAME}>
        <Row>
          <Col span={24}>
            <IbFormFieldLabel
              hint={t('Source settings: Common: Name: Hint')}
              label={t('Source settings: File: Name: Title')}
            />
            <IbInput
              status={errors.name.isTouched && errors.name.isError ? 'error' : 'default'}
              value={parameters.name}
              onBlur={onNameBlur}
              onChange={onNameChange}
            />
            {errors.name.isTouched && errors.name.isError && (
              <IbTypography.Paragraph error type="descriptor">
                {t('Source settings: File: Name: Error')}
              </IbTypography.Paragraph>
            )}
          </Col>
        </Row>
        <FileUpload multiple noButton accept={ACCEPTED_FILE_TYPES} disabled={loading} onUpload={onFileUpload}>
          <div className={FILE_UPLOAD_BUTTON_CLASS_NAME}>
            <IbButton icon={<IbIcon iconName="add-one" />} type="link">
              {t('Source settings: File: Upload: Button: Title')}
            </IbButton>
            <IbTypography.Paragraph disabled type="descriptor">
              {t('Source settings: File: Upload: Button: Description')}
            </IbTypography.Paragraph>
          </div>
        </FileUpload>
        {!!files.length && (
          <div>
            <IbTypography.Paragraph strong>
              {t('Source settings: File: Files: Title', { value: loading ? '?' : formatFileCount(files.length) })}
            </IbTypography.Paragraph>
            <FileTable items={files} loading={loading} onDelete={onDeleteClick} />
          </div>
        )}
        {errors.files.isTouched && errors.files.isError && (
          <IbTypography.Paragraph error type="descriptor">
            {t('Source settings: File: Files: Error')}
          </IbTypography.Paragraph>
        )}
      </div>
    );
  }
);

export default SourceSettingsFile;
